'use client';

import { type ReactNode, useState } from 'react';
import { useProductLinkNavigation } from '../../../product/hooks/useProductLinkNavigation';
import { useBrandStoreNavigation } from '../../hooks/useBrandStoreNavigation';
import { BrandMessageInput } from './common/BrandMessageInput';
import { CampaignDetailNotice } from './common/CampaignDetailNotice';
import { CampaignInfo } from './common/CampaignInfo';
import { ContentGuide } from './common/ContentGuide';
import { ContentSubmissionInput } from './common/ContentSubmissionInput';
import { ContentTypeSection } from './common/ContentTypeSection';
import { ProductCard } from './common/ProductCard';
import {
  groupProductCards,
  ProductCardGroup,
  ProductCardSectionTitle,
} from './common/ProductCardGroup';
import { SeedingCampaignDetailLayout } from './common/SeedingCampaignDetailLayout';
import { SeedingShippingSection } from './common/SeedingShippingSection';
import { ShippingTrackingCard } from './common/ShippingTrackingCard';
import { useCountdown } from './hooks/useCountdown';
import { CountdownHeader } from './recruit/CountdownHeader';
import { PROGRESS_TITLE_BY_STATUS } from './recruit/progressTitle';
import { RecruitApplyActionBar } from './recruit/RecruitApplyActionBar';
import { SectionDivider } from './shared/SectionDivider';
import {
  RECRUIT_STATUSES_WITH_APPLICATION_NUMBER,
  RECRUIT_STATUSES_WITH_COUNTDOWN_HEADER,
  RECRUIT_STATUSES_WITH_GUIDE_INFO_ICON,
  RECRUIT_STATUSES_WITH_INFO_CARD,
  RECRUIT_STATUSES_WITH_PRODUCT_CATEGORY,
  RECRUIT_STATUSES_WITH_PROGRESS_HEADER,
} from './shared/visibility';
import type {
  SeedingCampaignDetail,
  SeedingContentType,
  SeedingCtaAction,
  SeedingRecruitStatus,
  SeedingSnsChannel,
} from './types';

interface SeedingRecruitDetailPageViewProps {
  campaignId: number;
  detail: SeedingCampaignDetail;
  /** CTA 액션 핸들러 (라우터/API 연동은 호출 측에서 주입) */
  onCtaAction?: (action: SeedingCtaAction) => void;
  /** 옵션 선택 클릭 */
  onSelectProductOption?: (productId: number) => void;
  /** 배송지 변경 클릭 */
  onChangeAddress?: () => void;
  /** 콘텐츠 제출 항목 클릭 — content-submission 라우트 query 로 전달되는 채널 식별자 */
  onSubmitContent?: (channel: SeedingSnsChannel) => void;
  /** 콘텐츠 가이드 정보 아이콘 클릭 — 콘텐츠 등록 기간 안내 시트 노출 */
  onShowContentPeriod?: () => void;
  /**
   * 콘텐츠 유형(editable) 선택 변경 — 신청 payload 에 반영하기 위해 호출 측으로 올린다.
   * 선택값은 API enum(`SeedingContentType`), 미선택은 null.
   */
  onSelectContentType?: (type: SeedingContentType | null) => void;
  /** 브랜드 전달 메시지(free-text) 변경 — draft store 보존을 위해 호출 측으로 올린다. (SID-4157) */
  onChangeBrandMessage?: (message: string) => void;
  /**
   * 약관 동의 — 신청(BEFORE_APPLY) 화면에서만 전달.
   * `section`: 최하단 동의 섹션 UI, `primaryCtaDisabled`: 필수 미동의 시 신청 버튼 비활성.
   */
  agreement?: { section: ReactNode; primaryCtaDisabled?: boolean };
  /** 상품 썸네일 비율 — 29CM=square(5:5) / 무신사=portrait(세로). (SID-4424) */
  imageRatio?: 'square' | 'portrait';
  /**
   * 카운트다운 헤더("선정까지 남은 시간") 노출 억제. 신청 완료(/application) 페이지 전용.
   * 통일 상세(신청 내역)는 미전달(기본 false)이라 SID-4212 카운트다운 유지. (CMQA-7156 ②)
   */
  hideCountdownHeader?: boolean;
  className?: string;
}

export const SeedingRecruitDetailPageView = ({
  campaignId: _campaignId,
  detail,
  onCtaAction,
  onSelectProductOption,
  onChangeAddress,
  onSubmitContent,
  onShowContentPeriod,
  onSelectContentType,
  onChangeBrandMessage,
  agreement,
  imageRatio = 'portrait',
  hideCountdownHeader = false,
  className,
}: SeedingRecruitDetailPageViewProps) => {
  const [contentTypeSelection, setContentTypeSelection] = useState<SeedingContentType | null>(() =>
    detail.contentType.mode === 'editable' ? detail.contentType.selectedType : null,
  );
  const [brandMessage, setBrandMessage] = useState<string>(detail.contentType.message);

  // 상품 카드 브랜드명 클릭 → 브랜드 스토어 홈(새 웹뷰). 시딩=단일 브랜드라 캠페인 brandId 사용. (QAT-39448)
  const goToBrandStore = useBrandStoreNavigation();
  const brandId = detail.brand.brandId;
  const handleBrandClick = brandId ? () => goToBrandStore(brandId) : undefined;
  const navigateToProduct = useProductLinkNavigation();

  const status = detail.status as SeedingRecruitStatus;

  const showProgressHeader = RECRUIT_STATUSES_WITH_PROGRESS_HEADER.includes(status);
  const showCountdownHeader =
    !hideCountdownHeader &&
    !!detail.countdown &&
    RECRUIT_STATUSES_WITH_COUNTDOWN_HEADER.includes(status);
  const showInfoCard = RECRUIT_STATUSES_WITH_INFO_CARD.includes(status);
  const showProductCategory = RECRUIT_STATUSES_WITH_PRODUCT_CATEGORY.includes(status);

  const isEditableContentType = detail.contentType.mode === 'editable';
  const showBrandMessageEditable = isEditableContentType && status === 'recruiting';
  const isFrozenContentType = detail.contentType.mode === 'frozen';
  const useFrozenDetailContentType =
    isFrozenContentType &&
    detail.contentType.mode === 'frozen' &&
    !!detail.contentType.selectedSnsChannelLabel &&
    !!detail.contentType.uploadDeadlineNotice;

  const showContentSubmission = !!detail.contentSubmission;
  const showShippingTracking = !!detail.shippingTracking;

  const applicationNumber =
    detail.selectionNumber && RECRUIT_STATUSES_WITH_APPLICATION_NUMBER.includes(status)
      ? detail.selectionNumber
      : undefined;
  const showGuideInfoIcon = RECRUIT_STATUSES_WITH_GUIDE_INFO_ICON.includes(status);

  const handleProductAction = (productId: number) => {
    onSelectProductOption?.(productId);
  };

  // 마감 경과 시 신청 CTA 비활성 — useCountdown 1초 tick 으로 페이지 머문 중 0 도달해도 자동 disable.
  // 모집형 마감 = recruitmentEndAtMs(없으면 countdown.targetAt). action APPLY 인 CTA 에만 적용. (QAT-39695)
  const applyDeadlineAt = detail.recruitmentEndAtMs ?? detail.countdown?.targetAt ?? null;
  const isApplyDeadlinePassed = useCountdown({ targetAt: applyDeadlineAt })?.isExpired ?? false;

  const renderCta = () => {
    if (!detail.primaryCta) return null;
    const gateByDeadline = detail.primaryCta.primary.action === 'APPLY' && isApplyDeadlinePassed;
    return (
      <RecruitApplyActionBar
        cta={detail.primaryCta}
        primaryDisabled={(agreement?.primaryCtaDisabled ?? false) || gateByDeadline}
        onAction={(action) => onCtaAction?.(action)}
      />
    );
  };

  return (
    <SeedingCampaignDetailLayout className={className} showScrollToTop floatingCta={renderCta()}>
      {(showProgressHeader || showCountdownHeader || showInfoCard) && (
        <CampaignInfo.Container firstSection>
          {showProgressHeader && PROGRESS_TITLE_BY_STATUS[status] && (
            <CampaignInfo.Title>{PROGRESS_TITLE_BY_STATUS[status]}</CampaignInfo.Title>
          )}
          {showCountdownHeader && detail.countdown && (
            <CountdownHeader
              label={detail.countdown.label}
              targetAt={detail.countdown.targetAt}
              expiredLabel={detail.countdown.expiredLabel}
            />
          )}
          {showInfoCard && (
            <>
              <CampaignInfo.Card
                title={detail.campaignInfo.title}
                description={detail.campaignInfo.description}
              />
              <CampaignInfo.Table rows={detail.campaignInfo.infoRows} labelWidth={80} />
            </>
          )}
        </CampaignInfo.Container>
      )}

      {showContentSubmission && detail.contentSubmission && (
        <>
          <SectionDivider />
          <ContentSubmissionInput
            isComplete={detail.contentSubmission.isComplete}
            items={detail.contentSubmission.items.map((item) => ({
              ...item,
              onAction: (channel) => onSubmitContent?.(channel),
            }))}
          />
        </>
      )}

      <SectionDivider />

      {/* 바깥 Container = 그룹 간 간격(gap-y) 담당. 상단 Divider 와 겹치지 않도록 pt-12. (피드백 항목8) */}
      <CampaignInfo.Container>
        <ProductCardSectionTitle
          count={detail.products.length}
          guideText={detail.productSectionGuide ?? undefined}
          guideTextEmphasis={detail.productSectionGuideEmphasis ?? 'default'}
          // '제공 상품 N개'는 신청 전(recruiting)만. 신청 후(품절 포함)는 '상품 N개'. (SID-4317)
          provided={detail.status === 'recruiting'}
        />
        {groupProductCards(detail.products).map((group, groupIndex) => (
          <ProductCardGroup.Container key={`${group.category}-${groupIndex}`}>
            {/* 카테고리 헤더 노출 = 상태 게이트(showProductCategory) AND 데이터 category. */}
            {showProductCategory && group.category && (
              <ProductCardGroup.Title soldOut={group.items.every((item) => item.soldOut)}>
                {group.category}
              </ProductCardGroup.Title>
            )}
            <ProductCardGroup.List>
              {group.items.map((product) => (
                <ProductCard key={product.productId} soldOut={product.soldOut}>
                  <ProductCard.ImageInfoRow>
                    <ProductCard.Image
                      src={product.imageUrl}
                      alt={product.name}
                      soldOut={product.soldOut}
                      imageRatio={imageRatio}
                      onClick={() => navigateToProduct(product.webUrl)}
                    />
                    <ProductCard.Info
                      brand={product.brand}
                      name={product.name}
                      selectedOption={product.selectedOption ?? undefined}
                      price={product.price}
                      soldOut={product.soldOut}
                      onBrandClick={handleBrandClick}
                    />
                  </ProductCard.ImageInfoRow>
                  {/* 옵션 선택 동선을 처리할 핸들러가 있을 때만 버튼 노출 — 핸들러 없는 컨테이너
                      (통합 라우트 RecruitDetailContainer = 참여-후 read-only)에서 '눌러도 무반응'
                      버튼이 뜨던 문제 방지. (모집 미신청은 신청 라우트가 담당) */}
                  {product.actionLabel && !product.soldOut && onSelectProductOption && (
                    <ProductCard.Action onClick={() => handleProductAction(product.productId)}>
                      {product.actionLabel}
                    </ProductCard.Action>
                  )}
                </ProductCard>
              ))}
            </ProductCardGroup.List>
          </ProductCardGroup.Container>
        ))}
      </CampaignInfo.Container>

      <SectionDivider />

      {isEditableContentType && (
        <ContentTypeSection
          mode="radio"
          options={
            detail.contentType.mode === 'editable'
              ? detail.contentType.options.map((option) => ({
                  type: option.value,
                  label: option.label,
                  // 선택 시 우측에 "배송 완료일로부터 N일 이내 업로드" 노출. 누락 시 미표시. (SID-4369)
                  deadline: option.deadline,
                }))
              : []
          }
          selectedType={contentTypeSelection}
          onSelect={(type) => {
            setContentTypeSelection(type);
            onSelectContentType?.(type);
          }}
          footer={
            showBrandMessageEditable ? (
              // 브랜드 메시지 = 콘텐츠 유형 섹션의 형제 아이템 (라디오 직후 gap-8). (SID-4141/항목6·7)
              <BrandMessageInput
                embedded
                title="어떤 콘텐츠를 제작하실 예정인가요? (선택)"
                value={brandMessage}
                onChange={(value) => {
                  setBrandMessage(value);
                  // SNS 채널 등록 등 라우트 왕복 간 보존을 위해 draft store 로 끌어올린다. (SID-4157)
                  onChangeBrandMessage?.(value);
                }}
                placeholder={
                  detail.contentType.mode === 'editable'
                    ? detail.contentType.messagePlaceholder
                    : undefined
                }
                maxLength={
                  detail.contentType.mode === 'editable' ? detail.contentType.messageMaxLength : 200
                }
              />
            ) : undefined
          }
        />
      )}

      {useFrozenDetailContentType && detail.contentType.mode === 'frozen' && (
        <ContentTypeSection
          mode="frozen-detail"
          snsChannel={detail.contentType.selectedSnsChannelLabel ?? '-'}
          contentTypeLabel={detail.contentType.selectedTypeLabel}
          uploadDeadlineNotice={detail.contentType.uploadDeadlineNotice ?? '-'}
          brandMessage={detail.contentType.message || undefined}
        />
      )}

      {isFrozenContentType && !useFrozenDetailContentType && (
        <ContentTypeSection
          mode="info"
          rows={[
            {
              label: '선택한 콘텐츠 유형',
              value:
                detail.contentType.mode === 'frozen' ? detail.contentType.selectedTypeLabel : '-',
            },
            ...(detail.contentType.mode === 'frozen' && detail.contentType.message
              ? [{ label: '브랜드 메시지', value: detail.contentType.message }]
              : []),
          ]}
        />
      )}

      <SectionDivider />

      {/* 상단 Divider 와 겹치지 않도록 pt-24→pt-12. (피드백 항목9) */}
      <ContentGuide.Container>
        <ContentGuide.Title onInfoClick={showGuideInfoIcon ? onShowContentPeriod : undefined}>
          콘텐츠 제작 가이드
        </ContentGuide.Title>
        {/* 필수 해시태그 Subsection 내부 gap-y 8px. (피드백 항목10) */}
        <ContentGuide.Subsection
          label="필수 해시태그"
          subtitle={detail.contentGuide.hashtagNotice}
          className="gap-[8px]!"
        >
          <ContentGuide.HashtagBox text={detail.contentGuide.hashtagText} />
        </ContentGuide.Subsection>
        <ContentGuide.Subsection label="브랜드 요청사항" body={detail.contentGuide.brandRequest} />
        {applicationNumber && (
          <ContentGuide.Subsection label="신청 번호" body={applicationNumber} />
        )}
      </ContentGuide.Container>

      {showShippingTracking && detail.shippingTracking && (
        <>
          <SectionDivider />
          <ShippingTrackingCard
            statusLabel={detail.shippingTracking.statusLabel}
            labelVariant={detail.shippingTracking.labelVariant}
            banners={detail.shippingTracking.banners}
            details={detail.shippingTracking.details}
          />
        </>
      )}

      <SectionDivider />

      <SeedingShippingSection
        address={detail.shippingAddress}
        changeable={detail.shippingAddress.editable}
        onChangeAddress={onChangeAddress}
      />

      {/* 디자인 요청(순서 변경): 약관 동의 → 유의사항. 약관 동의는 자체 상단 SectionDivider 로 경계를
          만들고(없으면 아래 divider 가 유의사항 경계), 유의사항이 항상 마지막이라 하단 safe-area(pb-72)는
          유의사항(CampaignDetailNotice 자체 className)이 책임. */}
      {agreement?.section}

      <SectionDivider />

      <CampaignDetailNotice />
    </SeedingCampaignDetailLayout>
  );
};
